import React, { ReactNode } from 'react';
import { ApiPermission } from '@allbin/mobilix-api-client';
import { TestUserWorkorderAdminPermissions } from '../testdata/permissions';
import useOrganizationStore from '../store/organizationStore';
import useUserStore from '../store/user';

// Only side-effect-free testdata may be imported here (permissions.ts is
// pure const arrays) — testdata/users.tsx evaluates DateTime.now() on load,
// which would defeat tree-shaking in production builds.

// Matches tdUsers[0] / tdAllAuthProfiles[0] so msw self-lookups resolve.
const DEMO_UID = '6138bc01-fc28-4280-86ad-2fa430000001';
const DEMO_USER_PROFILE_ID = 'f0e1d2c3-0000-4000-8000-000000000001';
const DEMO_EMAIL = 'mobilix-workorder-admin@allbinary.se';
const DEMO_NAME = 'Workorder Administrator';

interface StubTokenClaims {
  sub?: string;
  email?: string;
  tenant_id?: string;
  organization_id?: string | null;
  permissions?: ApiPermission[];
  user_profile_id?: string;
}

const base64url = (s: string): string =>
  btoa(s).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');

// getValidToken only checks `exp` and the token is decoded with jwt-decode,
// so an unsigned far-future token keeps mock mode fully offline. The msw
// /profiles/token handler passes claims matching the selected profile.
export const buildStubToken = (claims: StubTokenClaims = {}): string => {
  const header = base64url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = base64url(
    JSON.stringify({
      sub: claims.sub ?? DEMO_UID,
      email: claims.email ?? DEMO_EMAIL,
      tenant_id: claims.tenant_id ?? 'lts',
      organization_id: claims.organization_id ?? null,
      permissions: claims.permissions ?? TestUserWorkorderAdminPermissions,
      user_profile_id: claims.user_profile_id ?? DEMO_USER_PROFILE_ID,
      exp: 253370764800,
      iat: 0,
    }),
  );
  return `${header}.${payload}.`;
};

// Also called by gallery-only components that need a signed-in store in
// galleryNoMsw mode, where this provider does not mount. Idempotent.
export const primeStubAuthStores = (): void => {
  const store = useUserStore.getState();
  store.setAccessToken(buildStubToken());
  store.setUserProfileId(DEMO_USER_PROFILE_ID);
  store.setRole('Administratör');
  store.setOrganization(null);
  store.setTenant('region-sormland');
  store.setName(DEMO_NAME);
  // RequireLogin skips the /tenants fetch in mock mode; prime the lookup
  // (inlined, not from testdata — see the tree-shaking note above).
  useOrganizationStore.getState().setOrgs([
    { id: 'region-sormland', tenant_id: 'lts', name: 'Region Sörmland' },
    { id: 'region-vastmanland', tenant_id: 'vl', name: 'Region Västmanland' },
  ]);
};

let userStorePrimed = false;

const StubAllAuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Prime the user store synchronously on first render so RequireLogin doesn't
  // flash the profile-selector screen on its way to the post-auth view. A
  // module-level guard makes this idempotent across StrictMode double-mounts.
  if (!userStorePrimed) {
    userStorePrimed = true;
    primeStubAuthStores();
  }

  return <>{children}</>;
};

export default StubAllAuthProvider;
